import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Settings, 
  Package, 
  Boxes,
  MessageSquare,
  Users,
  X,
  Cog,
  Factory,
  Cpu,
  Wrench
} from 'lucide-react';

const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();

  const navItems = [
    { 
      path: '/dashboard', 
      label: 'Dashboard', 
      icon: LayoutDashboard 
    },
    { 
      path: '/master', 
      label: 'Master Tables', 
      icon: Settings,
      children: [
        { path: '/master/machine-types', label: 'Machine Types', icon: Cog },
        { path: '/master/manufacturers', label: 'Manufacturers', icon: Factory },
        { path: '/master/models', label: 'Models', icon: Cpu },
        { path: '/master/part-names', label: 'Part Names', icon: Wrench },
      ]
    },
    { 
      path: '/products', 
      label: 'Products', 
      icon: Package 
    },
    { 
      path: '/prasco-products', 
      label: 'Prasco Products', 
      icon: Boxes 
    },
    { 
      path: '/inquiries/product', 
      label: 'Product Inquiries', 
      icon: MessageSquare 
    },
    { 
      path: '/inquiries/prasco', 
      label: 'Prasco Inquiries', 
      icon: MessageSquare 
    },
    { 
      path: '/leads', 
      label: 'Leads', 
      icon: Users 
    },
  ];

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const isMasterActive = () => {
    return location.pathname.startsWith('/master');
  };

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
          data-testid="sidebar-overlay"
        />
      )}
      
      <aside 
        className={`admin-sidebar ${isOpen ? 'open' : ''}`}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center">
              <Cog className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-lg text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Paras Hi-Tech
            </span>
          </div>
          <button 
            className="lg:hidden p-1 hover:bg-slate-100 rounded"
            onClick={onClose}
            data-testid="close-sidebar-btn"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {navItems.map((item) => (
            <React.Fragment key={item.path}>
              {item.children ? (
                <>
                  <div 
                    className={`nav-item cursor-default ${isMasterActive() ? 'text-slate-900' : ''}`}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </div>
                  <div className="ml-6">
                    {item.children.map((child) => (
                      <NavLink
                        key={child.path}
                        to={child.path}
                        className={({ isActive }) => 
                          `nav-item ${isActive ? 'active' : ''}`
                        }
                        onClick={onClose}
                        data-testid={`nav-${child.label.toLowerCase().replace(' ', '-')}`}
                      >
                        <child.icon className="w-4 h-4" />
                        <span className="text-sm">{child.label}</span>
                      </NavLink>
                    ))}
                  </div>
                </>
              ) : (
                <NavLink
                  to={item.path}
                  className={({ isActive }) => 
                    `nav-item ${isActive ? 'active' : ''}`
                  }
                  onClick={onClose}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </NavLink>
              )}
            </React.Fragment>
          ))}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200">
          <p className="text-xs text-slate-400 text-center">
            Admin Panel v1.0
          </p>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
